import { useState } from "react";
import { Check, Loader2, Package, ShieldAlert, X } from "lucide-react";
import {
  processMarketplaceRevenueSharePayouts,
  reviewMarketplaceBuyerReviewEntry,
  reviewMarketplaceQueueEntry,
  updateMarketplaceListingLifecycle,
  useMarketplaceBuyerReviewQueue,
  useMarketplaceGovernanceListings,
  useMarketplaceRevenueShareQueue,
  useMarketplaceReviewQueue,
  type MarketplaceBuyerReviewQueueEntry,
  type MarketplaceGovernanceListingEntry,
  type MarketplaceRevenueShareQueueEntry,
  type MarketplaceReviewQueueEntry,
} from "@/hooks/use-marketplace";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

function MarketplaceReviewSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-72" />
      <Skeleton className="h-10 rounded-lg" />
      <div className="grid gap-4 xl:grid-cols-2">
        {Array.from({ length: 4 }, (_, index) => (
          <SkeletonCard key={index} className="h-72" />
        ))}
      </div>
    </div>
  );
}

function ReviewQueueCard({
  entry,
  feedback,
  isSaving,
  onFeedbackChange,
  onDecide,
}: {
  entry: MarketplaceReviewQueueEntry;
  feedback: string;
  isSaving: boolean;
  onFeedbackChange: (value: string) => void;
  onDecide: (action: "approve" | "reject") => void;
}) {
  return (
    <article className="stagger-item rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
              Pending review
            </span>
            {entry.category ? (
              <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                {entry.category}
              </span>
            ) : null}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{entry.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Submitted by {entry.creatorName} on {new Date(entry.submittedAt).toLocaleDateString()}
            </p>
            <p className="text-xs text-muted-foreground">
              Submitting tenant {entry.creatorTenantId}
            </p>
            <p className="text-xs text-muted-foreground">
              Submission {entry.id}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-background px-3 py-2 text-right text-sm">
          <div className="font-semibold text-foreground">{entry.packageSummary.totalAssets} assets</div>
          <div className="text-xs text-muted-foreground">Updated {new Date(entry.updatedAt).toLocaleDateString()}</div>
        </div>
      </div>

      <p className="mt-4 text-sm leading-6 text-muted-foreground">
        {entry.description ?? "No creator description was included with this package submission."}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {entry.packageSummary.highlights.map((highlight) => (
          <span key={highlight} className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
            {highlight}
          </span>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-background p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Package composition</div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(entry.packageSummary.assetCounts).map(([assetType, count]) => (
            <div key={assetType} className="rounded-xl border border-border/70 bg-muted/20 px-3 py-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">{assetType}</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{count}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-background p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Moderator note</div>
        <textarea
          value={feedback}
          onChange={(event) => onFeedbackChange(event.target.value)}
          rows={3}
          className="mt-3 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
          placeholder="Leave a review note for the creator if you reject or want an audit breadcrumb."
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => onDecide("approve")}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Approve and publish
        </button>
        <button
          type="button"
          onClick={() => onDecide("reject")}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm font-medium text-destructive transition hover:bg-destructive/10 disabled:opacity-60"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          Reject
        </button>
      </div>
    </article>
  );
}

function BuyerReviewQueueCard({
  entry,
  feedback,
  isSaving,
  onFeedbackChange,
  onDecide,
}: {
  entry: MarketplaceBuyerReviewQueueEntry;
  feedback: string;
  isSaving: boolean;
  onFeedbackChange: (value: string) => void;
  onDecide: (action: "approve" | "reject") => void;
}) {
  return (
    <article className="stagger-item rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
              Pending buyer review
            </span>
            {entry.category ? (
              <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                {entry.category}
              </span>
            ) : null}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{entry.listingTitle}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Creator {entry.creatorName} • reviewer tenant {entry.reviewerTenantId}
            </p>
            <p className="text-xs text-muted-foreground">
              Listing {entry.listingId} • creator tenant {entry.creatorTenantId}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-background px-3 py-2 text-right text-sm">
          <div className="inline-flex items-center gap-1 font-semibold text-foreground">
            <Check className="h-4 w-4 text-[var(--color-warning)]" />
            {entry.rating.toFixed(1)}
          </div>
          <div className="text-xs text-muted-foreground">Submitted {new Date(entry.submittedAt).toLocaleDateString()}</div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-background p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Buyer review text</div>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {entry.reviewText?.trim() || "Rating submitted without review text."}
        </p>
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-background p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Moderator note</div>
        <textarea
          value={feedback}
          onChange={(event) => onFeedbackChange(event.target.value)}
          rows={3}
          className="mt-3 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
          placeholder="Leave a moderation note if this review should be removed."
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => onDecide("approve")}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Approve review
        </button>
        <button
          type="button"
          onClick={() => onDecide("reject")}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm font-medium text-destructive transition hover:bg-destructive/10 disabled:opacity-60"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          Remove review
        </button>
      </div>
    </article>
  );
}

function RevenueShareQueueCard({
  entry,
}: {
  entry: MarketplaceRevenueShareQueueEntry;
}) {
  return (
    <article className="stagger-item rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
              Pending creator payout
            </span>
            <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              {entry.currency}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{entry.creatorName}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Creator tenant {entry.creatorTenantId}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-background px-3 py-2 text-right text-sm">
          <div className="font-semibold text-foreground">
            {(entry.pendingPayout / 100).toLocaleString(undefined, {
              style: "currency",
              currency: entry.currency,
            })}
          </div>
          <div className="text-xs text-muted-foreground">{entry.shareCount} pending shares</div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-background p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Oldest pending</div>
          <p className="mt-2 text-sm text-foreground">{new Date(entry.oldestPendingAt).toLocaleString()}</p>
        </div>
        <div className="rounded-2xl border border-border bg-background p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Newest pending</div>
          <p className="mt-2 text-sm text-foreground">{new Date(entry.newestPendingAt).toLocaleString()}</p>
        </div>
      </div>
    </article>
  );
}

function GovernanceListingCard({
  entry,
  feedback,
  isSaving,
  onFeedbackChange,
  onAction,
}: {
  entry: MarketplaceGovernanceListingEntry;
  feedback: string;
  isSaving: boolean;
  onFeedbackChange: (value: string) => void;
  onAction: (action: "suspend" | "restore") => void;
}) {
  const isSuspended = entry.status === "suspended";

  return (
    <article className="stagger-item rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${isSuspended ? "border border-destructive/30 bg-destructive/10 text-destructive" : "border border-emerald-500/30 bg-emerald-500/10 text-emerald-700"}`}>
              {isSuspended ? "Suspended listing" : "Live listing"}
            </span>
            <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              {entry.visibility === "public" ? "Public visibility" : "Private visibility"}
            </span>
            {entry.category ? (
              <span className="rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                {entry.category}
              </span>
            ) : null}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{entry.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Creator {entry.creatorName} • tenant {entry.creatorTenantId}
            </p>
            <p className="text-xs text-muted-foreground">
              Listing {entry.id}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-background px-3 py-2 text-right text-sm">
          <div className="font-semibold text-foreground">{entry.installCount} installs</div>
          <div className="text-xs text-muted-foreground">
            {entry.reviewCount} reviews • {entry.rating.toFixed(1)} rating
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-background p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Published</div>
          <p className="mt-2 text-sm text-foreground">
            {entry.publishedAt ? new Date(entry.publishedAt).toLocaleString() : "Not published yet"}
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-background p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Last updated</div>
          <p className="mt-2 text-sm text-foreground">{new Date(entry.updatedAt).toLocaleString()}</p>
        </div>
      </div>

      {entry.moderationReason ? (
        <div className="mt-4 rounded-2xl border border-border bg-background p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Current moderation note</div>
          <p className="mt-2 text-sm text-muted-foreground">{entry.moderationReason}</p>
        </div>
      ) : null}

      <div className="mt-4 rounded-2xl border border-border bg-background p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {isSuspended ? "Restore note" : "Suspension note"}
        </div>
        <textarea
          value={feedback}
          onChange={(event) => onFeedbackChange(event.target.value)}
          rows={3}
          className="mt-3 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
          placeholder={isSuspended
            ? "Add context before restoring this listing to the public marketplace."
            : "Document why this listing is being suspended from the public marketplace."}
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => onAction(isSuspended ? "restore" : "suspend")}
          disabled={isSaving}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition disabled:opacity-60 ${isSuspended ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10"}`}
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : isSuspended ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
          {isSuspended ? "Restore listing" : "Suspend listing"}
        </button>
      </div>
    </article>
  );
}

export default function AdminMarketplaceReviewPage() {
  const { entries, isLoading, error, setEntries } = useMarketplaceReviewQueue();
  const {
    entries: buyerReviewEntries,
    isLoading: isBuyerReviewLoading,
    error: buyerReviewError,
    setEntries: setBuyerReviewEntries,
  } = useMarketplaceBuyerReviewQueue();
  const {
    entries: payoutEntries,
    isLoading: isPayoutLoading,
    error: payoutError,
    setEntries: setPayoutEntries,
  } = useMarketplaceRevenueShareQueue();
  const {
    entries: governanceEntries,
    isLoading: isGovernanceLoading,
    error: governanceError,
    setEntries: setGovernanceEntries,
  } = useMarketplaceGovernanceListings();
  const [feedbackById, setFeedbackById] = useState<Record<string, string>>({});
  const [buyerReviewFeedbackById, setBuyerReviewFeedbackById] = useState<Record<string, string>>({});
  const [governanceFeedbackById, setGovernanceFeedbackById] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savingBuyerReviewId, setSavingBuyerReviewId] = useState<string | null>(null);
  const [savingGovernanceId, setSavingGovernanceId] = useState<string | null>(null);
  const [isProcessingPayouts, setIsProcessingPayouts] = useState(false);
  const [lastProcessedPayoutBatch, setLastProcessedPayoutBatch] = useState<{
    payoutBatchId: string | null;
    totalCreators: number;
    totalShares: number;
    totalAmount: number;
    processedCreators: Array<{
      creatorTenantId: string;
      amount: number;
      currency: string;
      shareCount: number;
    }>;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const payoutSummary = payoutEntries.reduce((summary, entry) => {
    summary.totalPendingPayout += entry.pendingPayout;
    summary.totalPendingShares += entry.shareCount;
    return summary;
  }, {
    totalPendingPayout: 0,
    totalPendingShares: 0,
  });

  const handleDecision = async (entryId: string, action: "approve" | "reject") => {
    setSavingId(entryId);
    setSaveError(null);

    try {
      await reviewMarketplaceQueueEntry(entryId, action, feedbackById[entryId]);
      setEntries((current) => current.filter((entry) => entry.id !== entryId));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to record marketplace review decision");
    } finally {
      setSavingId(null);
    }
  };

  const handleBuyerReviewDecision = async (reviewId: string, action: "approve" | "reject") => {
    setSavingBuyerReviewId(reviewId);
    setSaveError(null);

    try {
      await reviewMarketplaceBuyerReviewEntry(reviewId, action, buyerReviewFeedbackById[reviewId]);
      setBuyerReviewEntries((current) => current.filter((entry) => entry.id !== reviewId));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to record marketplace buyer review decision");
    } finally {
      setSavingBuyerReviewId(null);
    }
  };

  const handleProcessPayouts = async () => {
    setIsProcessingPayouts(true);
    setSaveError(null);

    try {
      const result = await processMarketplaceRevenueSharePayouts();
      setLastProcessedPayoutBatch(result);
      setPayoutEntries([]);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to process marketplace creator payouts");
    } finally {
      setIsProcessingPayouts(false);
    }
  };

  const handleGovernanceAction = async (listingId: string, action: "suspend" | "restore") => {
    setSavingGovernanceId(listingId);
    setSaveError(null);

    try {
      const updated = await updateMarketplaceListingLifecycle(listingId, action, governanceFeedbackById[listingId]);
      setEntries((current) => current.filter((entry) => entry.id !== listingId));
      setGovernanceEntries((current) => current.map((entry) => (
        entry.id === listingId
          ? {
            ...entry,
            status: updated.status,
            visibility: updated.visibility,
            moderationReason: updated.moderationReason,
            updatedAt: new Date().toISOString(),
          }
          : entry
      )));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to update marketplace listing lifecycle");
    } finally {
      setSavingGovernanceId(null);
    }
  };

  if (isLoading || isBuyerReviewLoading || isPayoutLoading || isGovernanceLoading) {
    return <MarketplaceReviewSkeleton />;
  }

  return (
    <div className="space-y-6">
      <header className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-700">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">Marketplace review queue</h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              Moderate cross-tenant template submissions before they become public marketplace listings. This surface is backed by the live builder snapshot listings, not a parallel submission queue.
            </p>
          </div>
        </div>
      </header>

      {error ? (
        <div className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {buyerReviewError ? (
        <div className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {buyerReviewError}
        </div>
      ) : null}

      {payoutError ? (
        <div className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {payoutError}
        </div>
      ) : null}

      {governanceError ? (
        <div className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {governanceError}
        </div>
      ) : null}

      {saveError ? (
        <div className="rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {saveError}
        </div>
      ) : null}

      {entries.length === 0 ? (
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title="No submissions waiting on moderation"
          description="The review queue is live. New creator packages will appear here when they move into pending review."
          variant="info"
        />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {entries.map((entry) => (
            <ReviewQueueCard
              key={entry.id}
              entry={entry}
              feedback={feedbackById[entry.id] ?? ""}
              isSaving={savingId === entry.id}
              onFeedbackChange={(value) => {
                setFeedbackById((current) => ({ ...current, [entry.id]: value }));
              }}
              onDecide={(action) => {
                void handleDecision(entry.id, action);
              }}
            />
          ))}
        </div>
      )}

      {buyerReviewEntries.length === 0 ? (
        <EmptyState
          icon={<ShieldAlert className="h-8 w-8" />}
          title="No buyer reviews waiting on moderation"
          description="Pending marketplace buyer reviews will appear here before they affect public listing reputation."
          variant="info"
        />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {buyerReviewEntries.map((entry) => (
            <BuyerReviewQueueCard
              key={entry.id}
              entry={entry}
              feedback={buyerReviewFeedbackById[entry.id] ?? ""}
              isSaving={savingBuyerReviewId === entry.id}
              onFeedbackChange={(value) => {
                setBuyerReviewFeedbackById((current) => ({ ...current, [entry.id]: value }));
              }}
              onDecide={(action) => {
                void handleBuyerReviewDecision(entry.id, action);
              }}
            />
          ))}
        </div>
      )}

      <section className="space-y-4">
        <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-sm md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-foreground">Creator payout queue</h2>
            <p className="text-sm text-muted-foreground">
              Manually settle approved marketplace revenue shares into a paid batch. This updates marketplace payout truth without implying external transfer automation.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              void handleProcessPayouts();
            }}
            disabled={isProcessingPayouts || payoutEntries.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
          >
            {isProcessingPayouts ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Process pending creator payouts
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Creators awaiting payout</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{payoutEntries.length.toLocaleString()}</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Distinct creator ledger groups still waiting to move into a paid batch.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Pending payout total</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">
              {(payoutSummary.totalPendingPayout / 100).toLocaleString(undefined, {
                style: "currency",
                currency: "EUR",
              })}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Aggregate creator-share amount currently queued for manual settlement.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Pending revenue shares</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{payoutSummary.totalPendingShares.toLocaleString()}</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Individual approved revenue-share ledger rows that would be marked paid in the next batch.
            </p>
          </div>
        </div>

        {lastProcessedPayoutBatch ? (
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-1">
                <h3 className="text-base font-semibold text-foreground">Last processed payout batch</h3>
                <p className="text-sm text-muted-foreground">
                  Manual settlement completed on the mounted revenue-share runtime. This confirms what the batch recorded; it does not imply external transfer automation beyond the ledger update.
                </p>
              </div>
              <div className="rounded-xl border border-border bg-background px-4 py-3 text-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Batch id</div>
                <div className="mt-1 font-semibold text-foreground">{lastProcessedPayoutBatch.payoutBatchId ?? "No batch id returned"}</div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-border bg-background p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Creators processed</div>
                <div className="mt-2 text-2xl font-semibold text-foreground">{lastProcessedPayoutBatch.totalCreators.toLocaleString()}</div>
              </div>
              <div className="rounded-2xl border border-border bg-background p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Shares settled</div>
                <div className="mt-2 text-2xl font-semibold text-foreground">{lastProcessedPayoutBatch.totalShares.toLocaleString()}</div>
              </div>
              <div className="rounded-2xl border border-border bg-background p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Amount recorded</div>
                <div className="mt-2 text-2xl font-semibold text-foreground">
                  {(lastProcessedPayoutBatch.totalAmount / 100).toLocaleString(undefined, {
                    style: "currency",
                    currency: lastProcessedPayoutBatch.processedCreators[0]?.currency ?? "EUR",
                  })}
                </div>
              </div>
            </div>

            {lastProcessedPayoutBatch.processedCreators.length > 0 ? (
              <div className="mt-4 space-y-3">
                {lastProcessedPayoutBatch.processedCreators.map((creator) => (
                  <div
                    key={`${creator.creatorTenantId}-${creator.currency}`}
                    className="flex flex-col gap-2 rounded-2xl border border-border bg-background p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <div className="font-medium text-foreground">{creator.creatorTenantId}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {creator.shareCount.toLocaleString()} revenue share{creator.shareCount === 1 ? "" : "s"} marked paid
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-foreground">
                      {(creator.amount / 100).toLocaleString(undefined, {
                        style: "currency",
                        currency: creator.currency,
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {payoutEntries.length === 0 ? (
          <EmptyState
            icon={<Package className="h-8 w-8" />}
            title="No creator payouts waiting on settlement"
            description="Pending marketplace revenue shares will appear here before they move into a paid batch."
            variant="info"
          />
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {payoutEntries.map((entry) => (
              <RevenueShareQueueCard
                key={`${entry.creatorTenantId}:${entry.currency}`}
                entry={entry}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-foreground">Live listing governance</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Moderate already-published marketplace offers with explicit suspend and restore controls so creator-facing suspended lifecycle state matches a mounted admin action.
          </p>
        </div>

        {governanceEntries.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert className="h-8 w-8" />}
            title="No live listings need governance action"
            description="Published and suspended marketplace listings will appear here when operational review is needed."
            variant="info"
          />
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {governanceEntries.map((entry) => (
              <GovernanceListingCard
                key={entry.id}
                entry={entry}
                feedback={governanceFeedbackById[entry.id] ?? ""}
                isSaving={savingGovernanceId === entry.id}
                onFeedbackChange={(value) => {
                  setGovernanceFeedbackById((current) => ({ ...current, [entry.id]: value }));
                }}
                onAction={(action) => {
                  void handleGovernanceAction(entry.id, action);
                }}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
